import { useTranslation } from 'react-i18next'

import { Alert, Badge, Button, Flex, Icon, Tooltip, Typography } from '@percent/lemonade'
import { ConfigRow } from './ConfigRow'

type EditableConfigRowProps = {
  id: string
  label: string
  tooltip?: string
  isLoading: boolean
  error?: string
  isEditing: boolean
  isSaving: boolean
  saveDisabled?: boolean
  saveError?: string
  onEdit: () => void
  onSave: () => void
  onCancel: () => void
  displayContent?: React.ReactNode
  editContent: React.ReactNode
}

export function EditableConfigRow({
  id,
  label,
  tooltip,
  isLoading,
  error,
  isEditing,
  isSaving,
  saveDisabled,
  saveError,
  onEdit,
  onSave,
  onCancel,
  displayContent,
  editContent
}: EditableConfigRowProps) {
  const { t } = useTranslation()

  if (!isEditing) {
    return (
      <ConfigRow id={id} label={label} tooltip={tooltip} isLoading={isLoading} error={error}>
        <Flex align="center" gap={4}>
          {displayContent ?? <Badge variant="default">{t('typography.notConfigured')}</Badge>}
          <Button variant="secondary" size="small" onClick={onEdit}>
            {t('button.edit')}
          </Button>
        </Flex>
      </ConfigRow>
    )
  }

  const labelContent = (
    <Flex align="center" gap={1}>
      <Typography variant="default">{label}</Typography>
      {tooltip && (
        <Tooltip content={tooltip} placement="right">
          <span>
            <Icon name="info" size={4} color="gray500" />
          </span>
        </Tooltip>
      )}
    </Flex>
  )

  return (
    <Flex direction="column" gap={2} py={2} data-testid={id}>
      {labelContent}
      <Flex direction="column" gap={2}>
        {editContent}
      </Flex>
      {saveError && <Alert variant="error">{saveError}</Alert>}
      <Flex gap={2}>
        <Button variant="primary" size="small" loading={isSaving} onClick={onSave} disabled={saveDisabled}>
          {t('button.save')}
        </Button>
        <Button variant="secondary" size="small" onClick={onCancel} disabled={isSaving}>
          {t('button.cancel')}
        </Button>
      </Flex>
    </Flex>
  )
}
